import { t } from 'i18n';
import React, { ReactNode } from 'react';
import { Layout as DefaultLayout } from 'tenants/_template/desktop/_module/Layout';
import { styled, SvgIcon, ThemeProps } from '@teko/ui';
import { useGlobalData } from 'contexts';
import { HeadphoneOutlined } from '@teko/ds-icon';
import { TopbarButton } from '@teko/teko-ecommerce';
import { LanguageSelector } from 'components/desktop/organisms';

const rightMenu: Array<ReactNode> = [
  <TopbarButton
    key={1}
    href="tel:19007059"
    icon={<HeadphoneOutlined color="white" size={18} />}
    tracking={{
      isTrackContent: true,
      regionName: 'taskbar',
      contentName: 'hotlineSupportURL',
    }}
  >
    1900 7059
  </TopbarButton>,
  <LanguageSelector key={2} />,
];

const FooterWrapper = styled.div(
  ({ backgroundColor }: { backgroundColor?: string }) => `
    display: flex;
    padding-top: 24px;
    background-color: ${backgroundColor || '#fff'};
  `,
);
const Brand = styled.div`
  display: flex;
  height: 100%;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem 4rem;
`;
const LogoDesc = styled.div(
  ({ theme }: ThemeProps) => `
  text-transform: uppercase;
  color: ${theme?.brand.primary};
  font-weight: 500;
  margin-top: 1rem;
  font-size: 1.25rem;
`,
);
const FooterContent = styled.div`
  flex: 1;
  display: flex;
  justify-content: space-between;
  align-items: start;
  padding: 2rem 4rem 1rem 0;
  gap: 0.75rem;
`;
const Title = styled.div`
  text-transform: uppercase;
  font-weight: 500;
  font-size: 1rem;
  font-size: 1rem;
`;
const Info = styled.div`
  display: flex;
  align-items: center;
  margin: 0.5rem 0;
`;
const StyledLink = styled.a`
  color: #333;
  text-decoration: none;
  white-space: pre;

  &:hover {
    color: #eb1f3b;
  }
`;
const StyledIcon = styled(SvgIcon)`
  margin-right: 8px;
`;
const FooterInfoWrapper = styled(FooterWrapper)`
  gap: 0.75rem;
  padding: 2rem 3rem 4rem;
`;
const CompanyInfo = styled.div`
  width: 320px;
`;
const Address = styled.div`
  flex: 1;
  display: flex;
  gap: 16px;
  justify-content: space-around;
`;
const Verified = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
`;

interface CustomLayoutProps extends LayoutProps {
  forceLogin?: boolean;
}
export const Layout: React.FunctionComponent<CustomLayoutProps> = ({
  children,
  ...props
}: CustomLayoutProps) => {
  const { theme } = useGlobalData();
  const infos = [
    {
      label: t('Giới thiệu về Umbala'),
      link: '/gioi-thieu-umbala',
    },
    {
      label: t('Quy chế hoạt động'),
      link: '/quy-che-hoat-dong',
    },
    {
      label: t('Điều khoản giao dịch'),
      link: '/dieu-khoan-giao-dich',
    },
    {
      label: t('Thông báo từ Umbala'),
      link: '/thong-bao-tu-umbala',
    },
    {
      label: t('Câu hỏi thường gặp'),
      link: '/cau-hoi-thuong-gap',
    },
  ];
  const policies = [
    {
      label: t('Chính sách bảo mật thông tin'),
      link: '/chinh-sach-bao-mat-thong-tin',
    },
    {
      label: t('Chính sách hàng hóa'),
      link: '/chinh-sach-hang-hoa',
    },
    {
      label: t('Chính sách thành viên'),
      link: '/chinh-sach-thanh-vien',
    },
    {
      label: t('Chính sách thanh toán'),
      link: '/chinh-sach-thanh-toan',
    },
    {
      label: t('Chính sách đổi trả'),
      link: '/chinh-sach-doi-tra',
    },
    {
      label: t('Chính sách bảo hành'),
      link: '/chinh-sach-bao-hanh',
    },
  ];
  const guides = [
    {
      label: t('Hướng dẫn mua hàng'),
      link: '/huong-dan-mua-hang',
    },
    {
      label: t('Hướng dẫn sử dụng'),
      link: '/huong-dan-su-dung',
    },
  ];
  const follows = [
    {
      icon: 'logo-facebook',
      label: t('UMBALA VIỆT NAM'),
      link: 'https://www.facebook.com/UmbalaVietNam',
    },
    {
      icon: 'logo-youtube',
      label: t('UMBALA VIỆT NAM'),
      link: 'https://www.youtube.com/channel/UCTLe3hdNCmF_tjRv060LAqw',
    },
    {
      icon: 'logo-tiktok',
      label: t('UMBALA VIỆT NAM'),
      link: 'https://www.tiktok.com/@umbalavietnam',
    },
    {
      icon: 'logo-instagram',
      label: t('UMBALA VIỆT NAM'),
      link: 'https://www.instagram.com/umbala_vn',
    },
  ];

  const Footer = (
    <>
      <FooterWrapper>
        <Brand>
          <img
            src="https://shopfront-cdn.tekoapis.com/static/umbala/logo_lite.png"
            alt="Umbala Vietnam"
            width={96}
            height={96}
          />
          <LogoDesc>{t('Đặc Quyền Mua Sỉ')}</LogoDesc>
        </Brand>
        <FooterContent>
          <div>
            <Title>{t('Liên hệ')}</Title>
            <Info>{t('Hỗ trợ từ 8:00 - 22:00')}</Info>
            <Info>
              <StyledLink href="tel:19007059">
                {t('Tổng đài: 1900 7059')}
              </StyledLink>
            </Info>
            <Info>
              <StyledLink href="tel:02871097059">
                Hotline: (028) 7109 7059
              </StyledLink>
            </Info>
            <Info>
              <StyledLink href="tel:0936206602">
                {'                '}0936 206 602
              </StyledLink>
            </Info>
            <StyledLink href="mailto:info@umbala.vn">
              Email: info@umbala.vn
            </StyledLink>
          </div>
          <div>
            <Title>{t('Giới thiệu')}</Title>
            {infos.map((info) => (
              <Info key={info.link}>
                <StyledLink
                  href={info.link}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {info.label}
                </StyledLink>
              </Info>
            ))}
          </div>
          <div>
            <Title>{t('Chính sách')}</Title>
            {policies.map((info) => (
              <Info key={info.link}>
                <StyledLink
                  href={info.link}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {info.label}
                </StyledLink>
              </Info>
            ))}
          </div>
          <div>
            <Title>{t('Hướng dẫn')}</Title>
            {guides.map((info) => (
              <Info key={info.link}>
                <StyledLink
                  href={info.link}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {info.label}
                </StyledLink>
              </Info>
            ))}
          </div>
          <div>
            <Title>{t('Theo dõi chúng tôi')}</Title>
            {follows.map((info) => (
              <Info key={info.link}>
                <StyledIcon
                  name={info.icon}
                  size={16}
                  color={theme?.brand?.primary}
                />
                <StyledLink
                  href={info.link}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {info.label}
                </StyledLink>
              </Info>
            ))}
          </div>
        </FooterContent>
      </FooterWrapper>

      <FooterInfoWrapper backgroundColor="#e9edf2">
        <CompanyInfo>
          <Title>{t('Công ty cổ phần umbala việt nam')}</Title>
          <Info>{t('Mã số thuế: 0314329846')}</Info>
          <Info>{t('Địa chỉ: 292/15 Cách Mạng Tháng Tám')}</Info>
          <Info>{t('Phường 10, Quận 3, Thành phố Hồ Chí Minh')}</Info>
          <Info>
            <StyledLink href="mailto:umbala@umbala.vn">
              Email: umbala@umbala.vn
            </StyledLink>
          </Info>
          <Info>
            <StyledLink href="/">Website: umbala.vn</StyledLink>
          </Info>
          <Info>{t('App: Umbala Việt Nam')}</Info>
        </CompanyInfo>
        <Address>
          <div>
            <Title>{t('Hồ chí minh')}</Title>
            <div>
              {t('umbala.desktop._module.Layout.0')}
              <br />
              {t('Thành phố Hồ Chí Minh')}
            </div>
          </div>

          <div>
            <Title>{t('Đà Nẵng')}</Title>
            <div>
              {t('Tầng 2. Số 14-16 Nguyễn Văn Linh Phường Nam Dương')}
              <br />
              {t('Quận Hải Châu, TP. Đà Nẵng')}
            </div>
          </div>
          <Verified>
            <div>
              <Title>{t('Hà nội')}</Title>

              <div>
                {t('Tầng 6. Số 01 Thái Hà, Phường Trung Liệt')}
                <br />
                {t('Quận Đống Đa, Hà Nội')}
              </div>
            </div>
            <StyledLink
              href="http://online.gov.vn/Home/WebDetails/33227"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src="https://phongvu.vn/media/wysiwyg/phongvu/phongvu_v3/da-dang-ky.png"
                alt="Verified Logo"
                width={200}
              />
            </StyledLink>
          </Verified>
        </Address>
      </FooterInfoWrapper>
    </>
  );
  return (
    <DefaultLayout {...props} rightMenu={rightMenu} footer={Footer}>
      {children}
    </DefaultLayout>
  );
};
